import { Box, Button, Grid, IconButton, InputAdornment, Paper, Stack, TextField, Typography, useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useStudentActions } from "../../../hooks/useStudentActions";
import BackdropLoader from "../../../components/backdrop-loader";
import ResourcePreviewCard from "./components/resource-preview-card";
import { ArrowBack, Close, Description, Download, Info, Search } from "@mui/icons-material";
import { Resource, ResourceType } from "../../../models/resource";
import { RenderRichText } from "../../../components/render-rich-text";
import MobileResourceDrawer from "./components/mobile-resource-drawer";
import AccessExpired from "../../../components/access-expired";
import { isRichTextContentEmpty } from "../../../utils/rich-text-empty";

export default function CollectionDetails() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { classroom_id, collection_id } = useParams<{classroom_id: string, collection_id: string }>();

    const [loading, setLoading] = useState(true);    
    
    const {
        classrooms,
        getClassrooms,
        getCollections,
        getCollection,
    } = useStudentActions();

    const classroom = classrooms?.[Number(classroom_id)];
    const collection = classroom?.collections?.[Number(collection_id)];

    const [selectedResource, setSelectedResource] = useState<Resource | null>(null);

    const [searchTerm, setSearchTerm] = useState('');

    const navigate = useNavigate();

    const [mobileDrawer, setMobileDrawer] = useState(false);

    // State for the info dialog
    const [infoDialogOpen, setInfoDialogOpen] = useState(false);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value.toLowerCase());
    };

    const handleInfoClick = () => {
        setInfoDialogOpen(true);
    };

    const handleInfoDialogClose = () => {
        setInfoDialogOpen(false);
    };

    const downloadResource = async () => {
        if (!selectedResource) return;

        try {
            const response = await fetch(`https://d1at3arxf72ma9.cloudfront.net/classrooms/${classroom_id}/resources/${selectedResource.url}`, {
                mode: 'cors'
            });

            const blob = await response.blob();

            const extension = selectedResource.url.split('.').pop();
            const filename = `elms_${selectedResource.title}.${extension}`;
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('File download failed:', error);
        }
    };

    const readNote = () => {
        if (!selectedResource) return;

        navigate(`/classrooms/${classroom_id}/library/collections/${collection_id}/notes/?note_id=${selectedResource.url}`)
    };

    const closeResourceView = () => {
        setMobileDrawer(false);
        setSelectedResource(null);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    useEffect(() => {
        const loadData = async () => {
            if (!classrooms) await getClassrooms();

            if (classroom && !classroom.collections) await getCollections(Number(classroom_id));

            if (collection) {
                if (!collection.resources) await getCollection(Number(classroom_id), Number(collection_id));
                setLoading(false);
            }
        }

        loadData();
    }, [classrooms, classroom_id, collection_id]);

 
    if (!classroom || !collection || !collection.resources || loading) {
        return (<BackdropLoader/>)
    }

    if (classroom.expiry_date && new Date() >= new Date(classroom.expiry_date)) {
        return <AccessExpired classroom_id={classroom_id!}/>
    }

    const filteredResources = collection.resources.filter(resource =>
        resource.title.toLowerCase().includes(searchTerm)
    );

  
    return (
        <Box sx={{ 
            backgroundColor: 'grey.50',
            minHeight: '100vh'
        }}>

            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: 2,
                backgroundColor: '#1e293b',
            }}>

                <Box
                    component={Link}
                    to={`/classrooms/${classroom_id}#library`}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        textDecoration: 'none',
                        color: '#475569',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        minWidth: 0,
                        flex: 1,
                    }}
                >
                    <ArrowBack 
                        fontSize="small" 
                        sx={{ 
                            flexShrink: 0,
                            color: '#64748b',
                            transition: 'color 0.2s ease',
                        }} 
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '0.75rem',
                                fontWeight: 400,
                                color: '#64748b',
                                lineHeight: 1,
                                mb: 0.25,
                            }}
                        >
                            Collections
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                                color: '#f1f5f9',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {collection && collection.title || "Untitled Collection"}
                        </Typography>
                    </Box>
                </Box>

                <IconButton 
                    onClick={handleInfoClick}
                    size="small"
                    sx={{ 
                        color: '#64748b',
                        '&:hover': { 
                            color: 'primary.main',
                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                        }
                    }}
                >
                    <Info fontSize="small" />
                </IconButton>
            </Box>

            <Box
                sx={{
                    maxWidth: '1200px',
                    marginX: 'auto',
                    padding: 2,
                    paddingBottom: 4
                }}
            >
                <TextField
                    fullWidth
                    placeholder="Search resources..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    autoFocus
                    InputProps={{
                        sx: {
                            backgroundColor: "white",
                            borderRadius: 2,
                        },
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="primary" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderColor: 'grey.500',
                            borderRadius: 5,
                            height: 50,
                            marginY: 2,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'grey.600',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'grey.600',
                                borderWidth: '1px',
                            },
                        },
                        
                    }}
                />

                {filteredResources.length === 0 && (
                    <Box
                        sx={{
                            width: '100%',
                            py: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'text.secondary',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, mb: 1, color: 'grey.500' }}
                        >
                            No resources available
                        </Typography>
                    </Box>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2
                    }}
                >
                    
                    {selectedResource && !isMobile && (
                       <Box sx={{ flex: 0.6, overflowY: 'auto', px: 2 }}>
                            <Box
                                sx={{
                                    border: '1px solid rgba(0, 0, 0, 0.12)',
                                    borderRadius: 2,
                                    p: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        mb: 2,
                                    }}
                                >
                                    <IconButton
                                        onClick={closeResourceView}
                                        sx={{
                                            color: 'text.secondary',
                                            '&:hover': { color: 'text.primary' },
                                        }}
                                    >
                                        <Close />
                                    </IconButton>
                                </Box>

                                {selectedResource.type === ResourceType.MEDIA && (
                                <Box
                                    sx={{
                                        mb: 2,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        bgcolor: 'grey.50',
                                        border: '1px solid rgba(0,0,0,0.1)',
                                    }}
                                >
                                    {selectedResource.url.toLowerCase().endsWith('.mp4') ? (
                                        <video
                                            controls
                                            width="100%"
                                            key={selectedResource.url}
                                            style={{
                                                display: 'block',
                                                height: '400px',
                                                objectFit: 'fill',
                                            }}
                                        >
                                        <source
                                            src={`https://d1at3arxf72ma9.cloudfront.net/classrooms/${classroom_id}/resources/${selectedResource.url}`}
                                            type="video/mp4"
                                        />
                                        Your browser does not support the video tag.
                                    </video>
                                    ) : (
                                    <img
                                        src={`https://d1at3arxf72ma9.cloudfront.net/classrooms/${classroom_id}/resources/${selectedResource.url}`}
                                        alt="media"
                                        style={{
                                            width: '100%',
                                            height: '400px',
                                            display: 'block',
                                            objectFit: 'contain',
                                        }}
                                    />
                                    )}
                                </Box>
                                )}

                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                {selectedResource.title}
                                </Typography>

                                {!isRichTextContentEmpty(selectedResource.description) && (
                                    <Box
                                        sx={{
                                            p: 2,
                                            mb: 2,
                                            borderRadius: 2,
                                            backgroundColor: 'grey.50',
                                            border: '1px solid rgb(233, 232, 232)',
                                        }}
                                    >
                                        <RenderRichText jsonContent={selectedResource.description} />
                                    </Box>
                                )}

                                {selectedResource.type === ResourceType.FILE && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<Download />}
                                        onClick={downloadResource}
                                    >
                                        Download File
                                    </Button>
                                )}

                                {selectedResource.type === ResourceType.NOTE && (
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<Description />}
                                        onClick={readNote}
                                    >
                                        Read Note
                                    </Button>
                                )}
                            </Box>
                        </Box>

                    )}

                    <Box sx={{ flex: (selectedResource && !isMobile) ? 0.4 : 1, overflowY: 'auto' }}>
                        <Grid container spacing={2}>
                            {filteredResources
                            .sort((a, b) => new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime())
                            .map((resource) => (
                                <Grid key={resource.id} size={{ xs: 12, md: (selectedResource && !isMobile) ? 12 : 4 }}>
                                    <ResourcePreviewCard 
                                        classroom_id={classroom_id!}
                                        collection_id={collection_id!}
                                        resource={resource}
                                        onClick={() => {
                                            setSelectedResource(resource);
                                            setMobileDrawer(true);
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                </Box>
            </Box>

            {selectedResource && isMobile && (
                <MobileResourceDrawer
                    open={mobileDrawer}
                    setOpen={setMobileDrawer}
                    classroom_id={classroom_id!}
                    collection_id={collection_id!}
                    selectedResource={selectedResource}
                    readNote={readNote}
                    downloadFile={downloadResource}
                    closeDrawer={closeResourceView}
                />
            )}

            <Dialog
                open={infoDialogOpen}
                onClose={handleInfoDialogClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    pb: 1
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Collection Details
                    </Typography>
                    <IconButton 
                        onClick={handleInfoDialogClose}
                        sx={{ 
                            color: 'text.secondary',
                            '&:hover': { color: 'text.primary' }
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ pt: 1 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Title
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            {collection.title}
                        </Typography>
                    </Box>

                    {collection.description && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                Description
                            </Typography>
                            <Box sx={{ 
                                p: 2, 
                                backgroundColor: 'grey.50', 
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'grey.200'
                            }}>
                                <RenderRichText jsonContent={collection.description} />
                            </Box>
                        </Box>
                    )}

                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Last Updated
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            {formatDate(collection.last_updated)}
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button 
                        onClick={handleInfoDialogClose}
                        variant="contained"
                        sx={{ borderRadius: 2 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}